export type {
  // Branded ids
  VisitorId,
  AgentId,
  SessionId,
  MessageId,
  Timestamp,
  // Enums
  AgentAvailability,
  VisitorStatus,
  SessionMedium,
  SessionAuthor,
  // Domain objects
  VisitorIdentity,
  VisitorContext,
  ShopifyStorefrontContext,
  ChatMessage,
  ChatAttachment,
  MessageCitation,
  ReadMoreLink,
  AgentCapacity,
  SoftLock,
  RTCSessionDescriptionLike,
  RTCIceCandidateLike,
  // Event union + per-event types
  BusEvent,
  BusEventName,
  BusEventOf,
  VisitorPresenceEvent,
  VisitorChatRequestEvent,
  VisitorChatMessageEvent,
  VisitorChatEscalateEvent,
  AgentAvailabilityEvent,
  AgentPresenceEvent,
  SiteAvailabilityEvent,
  AgentEngageEvent,
  AgentEngageReleaseEvent,
  AgentChatMessageEvent,
  AgentInCallEvent,
  AgentTypingEvent,
  VisitorTypingEvent,
  MessageDeliveryEvent,
  CsatRequestEvent,
  MessageReceiptEvent,
  ScreenShareEvent,
  SessionStartEvent,
  SessionEndEvent,
  SessionDisconnectEvent,
  SessionReconnectEvent,
  AITakeoverEvent,
  AIEscalateEvent,
  AISummaryEvent,
  WebRTCOfferEvent,
  WebRTCAnswerEvent,
  WebRTCIceEvent,
  WebRTCInviteEvent,
  WebRTCInviteResponseEvent,
  WebRTCHangupEvent,
  AgentNotification,
  AgentNotificationEvent,
} from './schema';

export type {
  EventBus,
  BusListener,
  BusUnsubscribe,
  NetworkConditions,
  ConnectionState,
} from './mock-bus';

import { getBus as getMockBus, disposeBus as disposeMockBus } from './mock-bus';
import {
  createSocketBus,
  type EventBus as SocketEventBus,
  type SocketBusConfig,
} from './socket-bus';

// ─── Bus selection ───
// If `configureBus()` was called with a serverUrl, use the Socket.IO bus.
// Otherwise fall back to the WS-relay mock bus (Phase 3 dev behavior).

let socketConfig: SocketBusConfig | null = null;
const socketInstances = new Map<string, SocketEventBus>();

export function configureBus(config: SocketBusConfig): void {
  socketConfig = config;
}

export function getBus(name: string = 'prakash-livechat'): SocketEventBus {
  if (socketConfig) {
    let bus = socketInstances.get(name);
    if (bus) return bus;
    bus = createSocketBus(socketConfig);
    socketInstances.set(name, bus);
    return bus;
  }
  // Mock bus has the same shape; cast through unknown to avoid the
  // structural mismatch when the two return types diverge later.
  return getMockBus(name) as unknown as SocketEventBus;
}

export function disposeBus(name: string = 'prakash-livechat'): void {
  const sb = socketInstances.get(name);
  if (sb) {
    sb.close();
    socketInstances.delete(name);
    return;
  }
  disposeMockBus(name);
}

export { createSocketBus } from './socket-bus';
export type { SocketAuth, VisitorAuth, AgentAuth, SocketBusConfig, OutboxStorage } from './socket-bus';
